import React from 'react';

import { Identifiable, RecordModel } from '../core';
import SeoPagePreviews from '../../components/business/seo/SeoPagePreviews';
import SeoScoreBadge from '../../components/business/seo/SeoScoreBadge';

export interface ISeoMeta extends Identifiable {
  id: number;
  /** Set for static routes such as `/about-us`; null for content-record rows. */
  path: string | null;
  content_type: number | null;
  object_id: number | null;
  /** What this row describes — the path, or the target record's label. */
  target_label: string;
  og_image: string | null;
  canonical_path: string;
  noindex: boolean;
  focus_keyword: string;
  schema_override: any | null;
  /** Localized read fields, resolved for the request's `?lang=`. */
  title?: string | null;
  description?: string | null;
  created_at: string;
  modified_at: string;
}

/**
 * The "Page SEO" table — the static marketing routes (`/about-us`, `/faqs`, …), which
 * have no DB record of their own to hang metadata off.
 *
 * Deliberately driven by the generic `RecordTable` rather than a custom screen: the
 * localized `title__en` / `description__es` inputs, their AI magic-wand buttons, loading
 * existing translations on edit, and the change log all come for free from the OPTIONS
 * schema. Content records (listings, destinations, posts) use `SeoPanel` instead, since
 * their metadata is edited in the context of the record.
 *
 * `is_static_page=true` scopes the table to path rows, so content-record overrides don't
 * show up here as rows with an empty path.
 */
export const SeoPageModel: RecordModel<ISeoMeta> = {
  name: 'Page SEO',
  namePlural: 'Page SEO',
  path: 'seo/meta',
  selectQuery: '',
  tableQuery: '&is_static_page=true',
  toString: r => r.path || r.target_label || `#${r.id}`,
  primaryKeyName: 'id',
  allowSearch: true,
  showsChangeLog: true,
  drawerWidth: '75%',
  customTableMappings: {
    // Never relevant on a static-page row.
    content_type: { omit: true },
    object_id: { omit: true },
    target_label: { omit: true },
    schema_override: { omit: true },
    canonical_path: { omit: true },
    created_at: { omit: true },
    path: { title: 'Route', width: 200 },
    title: { title: 'Title', width: 260 },
    description: { title: 'Description' },
    focus_keyword: { title: 'Focus keyword', width: 160 },
    noindex: { title: 'No-index', width: 90, align: 'center' },
  },
  extraColumns: [
    {
      key: 'seo_score',
      title: 'Score',
      width: 160,
      render: (seoMeta: ISeoMeta) => <SeoScoreBadge seoMeta={seoMeta} />,
    },
  ],
  customCreateFields: [
    // A static-page row targets a route, never a record — hide the generic-relation
    // picker. `object_id` is dropped by RecordFieldsMapper whenever `content_type` is
    // present in the schema, so hiding this hides both.
    { name: 'content_type', component: null },
    { name: 'schema_override', component: null },
  ],
  createEndChildren: <SeoPagePreviews />,
  formWrapperStyle: { display: 'flex', flexDirection: 'row', gap: 40, maxWidth: 1400 },
  createCenterFieldsStyle: { flex: 10, minWidth: 380 },
};
